import { getLocale } from "../../i18n";
import { useTranslation } from "react-i18next";
import { translate as tr } from "../../i18n";
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from "react-router-dom";
import axios from 'axios';
import Sidebar from '../common/sidebar';
import Footer from '../common/footer';

const EMPTY_LINE = { description: '', quantity: '1', unit: 'hrs', unit_price: '0', tax_rate: '0' };
const GROUPING_OPTIONS = [
  { value: 'entry', get label() { return tr("Each entry"); } },
  { value: 'project', get label() { return tr("By project"); } },
  { value: 'day', get label() { return tr("By day"); } },
  { value: 'project_day', get label() { return tr("Project + day"); } },
  { value: 'summary', get label() { return tr("Single summary"); } },
];

function toAmount(value) {
  const n = Number(value || 0);
  return Number.isFinite(n) ? n : 0;
}

function formatCurrency(value, currency) {
  const safeCurrency = currency && String(currency).trim().length === 3 ? String(currency).trim().toUpperCase() : 'EUR';
  return new Intl.NumberFormat(getLocale(), {
    style: 'currency',
    currency: safeCurrency,
    minimumFractionDigits: 2,
  }).format(Number(value || 0));
}

function buildDate(days) {
  const date = new Date();
  date.setDate(date.getDate() + Number(days || 0));
  return date.toISOString().split('T')[0];
}

function formatStatus(status) {
  return String(status || 'DRAFT').split('_').join(' ');
}

function canEditInvoice(status) {
  return status === 'DRAFT' || status === 'ISSUED' || status === 'OVERDUE';
}

export default function InvoiceEditorPage() {
  useTranslation();
  const auth = useSelector((state) => state.auth);
  const history = useNavigate();
  const { invoiceId } = useParams();
  const isNew = !invoiceId || invoiceId === 'new';
  const [clients, setClients] = useState([]);
  const [projectOptions, setProjectOptions] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null);
  const toastTimerRef = useRef(null);
  const [sourceTimers, setSourceTimers] = useState([]);
  const [importMeta, setImportMeta] = useState(null);
  const [form, setForm] = useState({
    client_id: '',
    status: 'DRAFT',
    issue_date: new Date().toISOString().split('T')[0],
    due_date: '',
    service_period_start: '',
    service_period_end: '',
    currency: 'EUR',
    notes: '',
    payment_instructions: '',
    source_grouping: 'project',
    source_project_id: '',
    source_timer_ids: [],
    amount_paid: '0',
    line_items: [EMPTY_LINE],
  });

  const requestConfig = useMemo(() => ({
    headers: {
      Authorization: `Bearer ${auth.token}`,
      'Content-Type': 'application/json',
    },
  }), [auth.token]);

  const showToast = useCallback((message, tone = 'success') => {
    setToast({ message, tone });
    window.clearTimeout(toastTimerRef.current);
    toastTimerRef.current = window.setTimeout(() => setToast(null), 3000);
  }, []);

  useEffect(() => () => window.clearTimeout(toastTimerRef.current), []);

  const applySettingsDefaults = useCallback((settingsData) => {
    setSettings(settingsData);
    setForm((prev) => ({
      ...prev,
      currency: prev.currency || settingsData.default_currency || 'EUR',
      due_date: prev.due_date || buildDate(settingsData.default_payment_terms_days || 14),
      payment_instructions: prev.payment_instructions || settingsData.payment_instructions || '',
    }));
  }, []);

  const loadBaseData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [clientRes, settingsRes] = await Promise.all([
        axios.post('/api/client/list', JSON.stringify({ workspace_id: auth.workspace_id, searchTerm: '' }), requestConfig),
        axios.get(`/api/invoice/settings?workspace_id=${auth.workspace_id}`, requestConfig),
      ]);

      setClients(clientRes.data.results || []);
      applySettingsDefaults(settingsRes.data.settings || {});

      if (!isNew) {
        const detailRes = await axios.post('/api/invoice/details', JSON.stringify({
          workspace_id: auth.workspace_id,
          invoice_id: invoiceId,
        }), requestConfig);
        const invoice = detailRes.data.invoice;
        if (!canEditInvoice(invoice.status)) {
          history(`/invoices/${invoiceId}`);
          return;
        }
        setForm({
          client_id: String(invoice.client_id),
          status: invoice.status || 'DRAFT',
          issue_date: invoice.issue_date ? new Date(invoice.issue_date).toISOString().split('T')[0] : '',
          due_date: invoice.due_date ? new Date(invoice.due_date).toISOString().split('T')[0] : '',
          service_period_start: invoice.service_period_start ? new Date(invoice.service_period_start).toISOString().split('T')[0] : '',
          service_period_end: invoice.service_period_end ? new Date(invoice.service_period_end).toISOString().split('T')[0] : '',
          currency: invoice.currency || 'EUR',
          notes: invoice.notes || '',
          payment_instructions: invoice.payment_instructions || '',
          source_grouping: 'project',
          source_project_id: '',
          source_timer_ids: [],
          amount_paid: String(invoice.amount_paid || '0'),
          line_items: (invoice.lineItems || []).length > 0
            ? invoice.lineItems.map((item) => ({
              description: item.description || '',
              quantity: String(item.quantity || '1'),
              unit: item.unit || 'hrs',
              unit_price: String(item.unit_price || '0'),
              tax_rate: String(item.tax_rate || '0'),
              project_id: item.project_id ? String(item.project_id) : '',
              timer_id: item.timer_id ? String(item.timer_id) : '',
            }))
            : [EMPTY_LINE],
        });
      }
    } catch (err) {
      setError('Failed to load invoice workspace data.');
    } finally {
      setLoading(false);
    }
  }, [applySettingsDefaults, auth.workspace_id, history, invoiceId, isNew, requestConfig]);

  useEffect(() => {
    loadBaseData();
  }, [loadBaseData]);

  useEffect(() => {
    async function loadProjectOptions() {
      if (!form.client_id) {
        setProjectOptions([]);
        return;
      }

      try {
        const body = JSON.stringify({
          workspace_id: auth.workspace_id,
          mode: 1,
          clients: form.client_id,
          projectTerm: '',
        });
        const res = await axios.post('/api/project/list', body, requestConfig);
        setProjectOptions(res.data.results || []);
      } catch (err) {
        setProjectOptions([]);
      }
    }

    loadProjectOptions();
  }, [auth.workspace_id, form.client_id, requestConfig]);

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const updateLine = (index, key, value) => {
    setForm((prev) => ({
      ...prev,
      line_items: prev.line_items.map((line, lineIndex) => (
        lineIndex === index ? { ...line, [key]: value } : line
      )),
    }));
  };

  const addLine = () => {
    setForm((prev) => ({ ...prev, line_items: [...prev.line_items, { ...EMPTY_LINE }] }));
  };

  const removeLine = (index) => {
    setForm((prev) => ({
      ...prev,
      line_items: prev.line_items.length === 1 ? prev.line_items : prev.line_items.filter((_, lineIndex) => lineIndex !== index),
    }));
  };

  const totals = useMemo(() => {
    const subtotal = form.line_items.reduce((sum, line) => sum + (toAmount(line.quantity) * toAmount(line.unit_price)), 0);
    const tax = form.line_items.reduce((sum, line) => {
      const lineTotal = toAmount(line.quantity) * toAmount(line.unit_price);
      return sum + (lineTotal * toAmount(line.tax_rate) / 100);
    }, 0);
    return {
      subtotal,
      tax,
      total: subtotal + tax,
    };
  }, [form.line_items]);

  const selectedClient = clients.find((client) => String(client.id) === String(form.client_id));
  const availableProjectOptions = projectOptions.length
    ? projectOptions
    : (selectedClient?.projects || []);

  const handleImportBillable = async () => {
    if (!form.client_id || !form.service_period_start || !form.service_period_end) {
      setError('Choose a client and service period before importing billable time.');
      showToast('Choose a client and service period before importing billable time.', 'danger');
      return;
    }

    setImporting(true);
    setError('');
    try {
      const params = new URLSearchParams({
        workspace_id: auth.workspace_id,
        client_id: form.client_id,
        from: form.service_period_start,
        to: form.service_period_end,
        grouping: form.source_grouping,
      });
      if (form.source_project_id) {
        params.set('project_id', form.source_project_id);
      }
      const res = await axios.get(`/api/invoice/billable-preview?${params.toString()}`, requestConfig);
      const rows = res.data.results || [];
      setImportMeta(res.data.meta || null);
      setSourceTimers(rows);
      if (!rows.length) {
        showToast('No billable time entries found for that service period.', 'warning');
        return;
      }

      setForm((prev) => ({
        ...prev,
        line_items: rows.map((item) => ({
          description: item.description,
          quantity: String(item.quantity),
          unit: item.unit || 'hrs',
          unit_price: String(item.unit_price || 0),
          tax_rate: String(item.tax_rate || 0),
          project_id: item.project_id ? String(item.project_id) : '',
          timer_id: item.timer_ids && item.timer_ids.length === 1 ? String(item.timer_ids[0]) : '',
        })),
        source_timer_ids: rows.flatMap((item) => item.timer_ids || []),
      }));
      showToast(`Imported ${rows.length} invoice line item${rows.length === 1 ? '' : 's'} from billable time.`, 'success');
    } catch (err) {
      setError('Failed to import billable time entries.');
      showToast('Failed to import billable time entries.', 'danger');
    } finally {
      setImporting(false);
    }
  };

  const handleSave = async (nextStatus = form.status) => {
    if (!form.client_id) {
      setError('Choose a client before saving the invoice.');
      showToast('Choose a client before saving the invoice.', 'danger');
      return;
    }

    if (form.line_items.some((item) => !item.description.trim())) {
      setError('Each invoice line item needs a description.');
      showToast('Each invoice line item needs a description.', 'danger');
      return;
    }

    setSaving(true);
    setError('');
    try {
      const payload = {
        ...form,
        status: nextStatus,
        workspace_id: auth.workspace_id,
        source_timer_ids: form.source_timer_ids,
        amount_paid: form.amount_paid,
        line_items: form.line_items,
      };
      if (isNew) {
        const res = await axios.post('/api/invoice/create', JSON.stringify(payload), requestConfig);
        showToast(nextStatus === 'ISSUED' ? 'Invoice created and issued successfully.' : 'Invoice draft created successfully.', 'success');
        history(`/invoices/${res.data.invoice.id}`); // goes to detail page
      } else {
        await axios.put('/api/invoice/update', JSON.stringify({
          ...payload,
          invoice_id: invoiceId,
        }), requestConfig);
        if (nextStatus !== form.status) {
          await axios.put('/api/invoice/status', JSON.stringify({
            invoice_id: invoiceId,
            workspace_id: auth.workspace_id,
            status: nextStatus,
          }), requestConfig);
          setForm((prev) => ({ ...prev, status: nextStatus }));
        }
        showToast(nextStatus === 'ISSUED' ? 'Invoice changes saved successfully.' : 'Invoice draft saved successfully.', 'success');
      }
    } catch (err) {
      const message = err.response?.data?.errors?.[0]?.msg || 'Failed to save invoice.';
      setError(message);
      showToast(message, 'danger');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <Sidebar />
      {toast ? (
        <div className="ti-toast-container">
          <div className={`ti-toast ti-toast--${toast.tone}`}>
            <span className="ti-toast-msg">{toast.message}</span>
          </div>
        </div>
      ) : null}
      <div className='content-wrap' id='wrapper'>
        <div className='main'>
          <div className='cp-shell'>
            <div className='cp-topbar'>
              <button type='button' className='cp-back' onClick={() => history(isNew ? '/invoices' : `/invoices/${invoiceId}`)}>
                {isNew ? tr("Back to Invoices") : tr("Back to Invoice")}
              </button>
              <div className='cp-actions'>
                <button type='button' className='cp-btn cp-btn--secondary' onClick={() => history('/invoice-settings')}>{tr("Invoice Settings")} </button>
                {form.status === 'DRAFT' ? (
                  <>
                    <button type='button' className='cp-btn cp-btn--secondary' onClick={() => handleSave('DRAFT')} disabled={saving || loading}>
                      {saving ? tr("Saving...") : tr("Save Draft")}
                    </button>
                    <button type='button' className='cp-btn cp-btn--primary' onClick={() => handleSave('ISSUED')} disabled={saving || loading}>
                      {saving ? tr("Saving...") : isNew ? tr("Create & Issue") : tr("Save & Issue")}
                    </button>
                  </>
                ) : (
                  <button type='button' className='cp-btn cp-btn--primary' onClick={() => handleSave(form.status || tr("ISSUED"))} disabled={saving || loading}>
                    {saving ? tr("Saving...") : tr("Save Changes")}
                  </button>
                )}
              </div>
            </div>

            <div className='cp-hero'>
              <div className='cp-hero-copy'>
                <div className='cp-eyebrow'>{tr("Invoice Builder")}</div>
                <h1>{isNew ? tr("Create a professional invoice") : `Edit ${selectedClient?.company_name || selectedClient?.client_name || tr("invoice draft")}`}</h1>
                <p>{tr("Build structured client invoices with line items, finance-ready metadata, and clear billing control.")}</p>
                <div className='iv-editor-badges'>
                  <span className='iv-editor-badge'>{selectedClient ? (selectedClient.company_name || selectedClient.client_name) : tr("Choose client")}</span>
                  <span className='iv-editor-badge'>{form.service_period_start && form.service_period_end ? `${form.service_period_start} to ${form.service_period_end}` : tr("Set service period")}</span>
                  <span className='iv-editor-badge'>{form.status === 'DRAFT' ? tr("Draft workflow") : formatStatus(form.status)}</span>
                </div>
              </div>
              <div className='cp-summary-card iv-editor-summary'>
                <div className='iv-editor-summary__eyebrow'>{tr("Invoice total")}</div>
                <strong className='pm-readiness-value'>{formatCurrency(totals.total, form.currency)}</strong>
                <div className='cp-summary-meta'>{tr("Draft workflow ·")} {tr("innerLineItems", { count: form.line_items.length })}</div>
                <div className='iv-editor-summary__stats'>
                  <div>
                    <span>{tr("Subtotal")}</span>
                    <strong>{formatCurrency(totals.subtotal, form.currency)}</strong>
                  </div>
                  <div>
                    <span>{tr("Tax")}</span>
                    <strong>{formatCurrency(totals.tax, form.currency)}</strong>
                  </div>
                </div>
              </div>
            </div>

            {error && <div className='iv-error'>{error}</div>}

            {loading ? (
              <div className='cp-loading-card'>{tr("Loading invoice builder...")}</div>
            ) : (
              <div className='cp-grid'>
                <div className='cp-main'>
                  <div className='pm-section'>
                    <div className='pm-section-head'>
                      <h4>{tr("Invoice Identity")}</h4>
                      <p>{tr("Anchor the draft to the right client, service window, and billing dates.")}</p>
                    </div>
                    <div className='pm-grid pm-grid--3'>
                      <div className='pm-field'>
                        <label className='pm-label'>{tr("Client")}</label>
                        <select
                          className='pm-input'
                          value={form.client_id}
                          onChange={(e) => {
                            const nextClientId = e.target.value;
                            setSourceTimers([]);
                            setImportMeta(null);
                            setForm((prev) => ({
                              ...prev,
                              client_id: nextClientId,
                              source_project_id: '',
                              source_timer_ids: [],
                            }));
                          }}
                        >
                          <option value=''>{tr("Select client")}</option>
                          {clients.map((client) => (
                            <option key={client.id} value={client.id}>{client.company_name || client.client_name}</option>
                          ))}
                        </select>
                      </div>
                      <div className='pm-field'>
                        <label className='pm-label'>{tr("Issue date")}</label>
                        <input className='pm-input' type='date' value={form.issue_date} onChange={(e) => updateField('issue_date', e.target.value)} />
                      </div>
                      <div className='pm-field'>
                        <label className='pm-label'>{tr("Due date")}</label>
                        <input className='pm-input' type='date' value={form.due_date} onChange={(e) => updateField('due_date', e.target.value)} />
                      </div>
                      <div className='pm-field'>
                        <label className='pm-label'>{tr("Service period start")}</label>
                        <input className='pm-input' type='date' value={form.service_period_start} onChange={(e) => updateField('service_period_start', e.target.value)} />
                      </div>
                      <div className='pm-field'>
                        <label className='pm-label'>{tr("Service period end")}</label>
                        <input className='pm-input' type='date' value={form.service_period_end} onChange={(e) => updateField('service_period_end', e.target.value)} />
                      </div>
                      <div className='pm-field'>
                        <label className='pm-label'>{tr("Currency")}</label>
                        <select className='pm-input' value={form.currency} onChange={(e) => updateField('currency', e.target.value)}>
                          {['EUR', 'USD', 'GBP', 'CHF'].map((item) => (
                            <option key={item} value={item}>{item}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className='pm-section'>
                    <div className='pm-section-head'>
                      <h4>{tr("Generate From Billable Time")}</h4>
                      <p>{tr("Use the service period above to pull uninvoiced billable entries and convert them into invoice lines.")}</p>
                    </div>
                    <div className='pm-grid pm-grid--2'>
                      <div className='pm-field'>
                        <label className='pm-label'>{tr("Grouping")}</label>
                        <select className='pm-input' value={form.source_grouping} onChange={(e) => updateField('source_grouping', e.target.value)}>
                          {GROUPING_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                      </div>
                      <div className='pm-field'>
                        <label className='pm-label'>{tr("Project scope")}</label>
                        <select
                          className='pm-input'
                          value={form.source_project_id}
                          onChange={(e) => updateField('source_project_id', e.target.value)}
                        >
                          <option value=''>{tr("All client projects")}</option>
                          {availableProjectOptions.map((project) => (
                            <option key={project.id} value={project.id}>{project.project_name}</option>
                          ))}
                          {form.client_id && availableProjectOptions.length === 0 ? (
                            <option value='' disabled>{tr("No projects found for this client")}</option>
                          ) : null}
                        </select>
                      </div>
                    </div>
                    <div className='pm-grid pm-grid--2'>
                      <div className='pm-field iv-import-actions'>
                        <label className='pm-label'>{tr("Billable source")}</label>
                        <button type='button' className='cp-btn cp-btn--secondary' onClick={handleImportBillable} disabled={importing}>
                          {importing ? tr("Importing...") : tr("Import Time Entries")}
                        </button>
                      </div>
                    </div>
                    {!!sourceTimers.length && (
                      <div className='iv-source-preview'>
                        <div className='iv-source-preview__head'>
                          <strong>
                            {tr('innerBillableImported', { count: importMeta?.timer_count || form.source_timer_ids.length })} </strong>
                          <span>{tr('innerInvoiceLines', { count: importMeta?.line_count || sourceTimers.length })} </span>
                        </div>
                        <div className='iv-source-preview__list'>
                          {sourceTimers.slice(0, 6).map((item) => (
                            <div key={`${item.description}-${item.source_count}-${item.project_id || 'none'}`} className='iv-source-chip'>
                              <span>{item.project_name || tr("No project")}</span>
                              <strong>{item.description}</strong>
                              <em>{tr('innerSourceEntries', { count: item.source_count })}</em>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className='pm-section'>
                    <div className='pm-section-head'>
                      <h4>{tr("Line Items")}</h4>
                      <p>{tr("Shape the invoice exactly how the client should understand and approve the work.")}</p>
                    </div>
                    <div className='iv-line-toolbar'>
                      <div className='iv-line-toolbar__copy'>
                        <strong>{tr("innerLineItems", { count: form.line_items.length })}</strong>
                        <span>{tr("Keep descriptions client-facing and totals easy to review.")}</span>
                      </div>
                    </div>
                    <div className='iv-line-list'>
                      {form.line_items.map((line, index) => {
                        const lineTotal = toAmount(line.quantity) * toAmount(line.unit_price);
                        return (
                          <div key={`invoice-line-${index}`} className='iv-line-card'>
                            <div className='iv-line-card__head'>
                              <span className='iv-line-card__index'>{tr("Line")} {index + 1}</span>
                              <button type='button' className='iv-line-remove' onClick={() => removeLine(index)} disabled={form.line_items.length === 1}>{tr("Remove")} </button>
                            </div>
                            <div className='iv-line-grid'>
                              <div className='pm-field iv-line-description'>
                                <label className='pm-label'>{tr("Description")}</label>
                                <input className='pm-input' type='text' value={line.description} onChange={(e) => updateLine(index, 'description', e.target.value)} placeholder={tr("Strategy workshop, monthly retainer, design sprint...")} />
                              </div>
                              <div className='pm-field'>
                                <label className='pm-label'>{tr("Qty")}</label>
                                <input className='pm-input' type='number' min='0' step='0.25' value={line.quantity} onChange={(e) => updateLine(index, 'quantity', e.target.value)} />
                              </div>
                              <div className='pm-field'>
                                <label className='pm-label'>{tr("Unit")}</label>
                                <input className='pm-input' type='text' value={line.unit} onChange={(e) => updateLine(index, 'unit', e.target.value)} />
                              </div>
                              <div className='pm-field'>
                                <label className='pm-label'>{tr("Unit price")}</label>
                                <input className='pm-input' type='number' min='0' step='0.01' value={line.unit_price} onChange={(e) => updateLine(index, 'unit_price', e.target.value)} />
                              </div>
                              <div className='pm-field'>
                                <label className='pm-label'>{tr("Tax %")}</label>
                                <input className='pm-input' type='number' min='0' step='0.01' value={line.tax_rate} onChange={(e) => updateLine(index, 'tax_rate', e.target.value)} />
                              </div>
                              <div className='iv-line-total'>
                                <span>{tr("Line total")}</span>
                                <strong>{formatCurrency(lineTotal, form.currency)}</strong>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <button type='button' className='cp-btn cp-btn--secondary iv-add-line' onClick={addLine}>{tr("Add line item")}</button>
                  </div>

                  <div className='pm-section'>
                    <div className='pm-section-head'>
                      <h4>{tr("Notes & Payment Instructions")}</h4>
                      <p>{tr("Give clients the operational details they need to process payment quickly.")}</p>
                    </div>
                    <div className='pm-grid pm-grid--2'>
                      <div className='pm-field pm-field--wide'>
                        <label className='pm-label'>{tr("Internal / client notes")}</label>
                        <textarea className='pm-input pm-input--textarea' rows='4' value={form.notes} onChange={(e) => updateField('notes', e.target.value)} placeholder={tr("Optional scope notes, milestone references, or invoice context.")} />
                      </div>
                      <div className='pm-field pm-field--wide'>
                        <label className='pm-label'>{tr("Payment instructions")}</label>
                        <textarea className='pm-input pm-input--textarea' rows='4' value={form.payment_instructions} onChange={(e) => updateField('payment_instructions', e.target.value)} placeholder={tr("Bank details, remittance instructions, or finance team guidance.")} />
                      </div>
                    </div>
                  </div>
                </div>

                <div className='cp-side'>
                  {!isNew && (
                    <div className='cp-side-card'>
                      <h3>{tr("Lifecycle guardrails")}</h3>
                      <div className='iv-payment-box'>
                        <div className='iv-payment-row'>
                          <span>Status</span>
                          <strong>{formatStatus(form.status)}</strong>
                        </div>
                        <p className='iv-side-note'>{tr("Drafts are fully editable. Once issued, payments and lifecycle actions are managed from the invoice detail page so financial history stays server-controlled.")}</p>
                      </div>
                    </div>
                  )}

                  <div className='cp-side-card'>
                    <h3>{tr("Client billing snapshot")}</h3>
                    {selectedClient ? (
                      <div className='iv-snapshot'>
                        <strong>{selectedClient.company_name || selectedClient.client_name}</strong>
                        <p>{selectedClient.billing_contact_name || tr("No billing contact yet")}</p>
                        <p>{selectedClient.billing_email || tr("No billing email yet")}</p>
                        <p>
                          {[
                            selectedClient.billing_address_line1,
                            selectedClient.billing_city,
                            selectedClient.billing_postal_code,
                            selectedClient.billing_country,
                          ].filter(Boolean).join(', ') || tr("Billing address still incomplete")}
                        </p>
                      </div>
                    ) : (
                      <p>{tr("Select a client to preview the billing profile that will be snapshotted into this invoice.")}</p>
                    )}
                  </div>

                  <div className='cp-side-card'>
                    <h3>{tr("Totals")}</h3>
                    <div className='iv-totals'>
                      <div><span>{tr("Subtotal")}</span><strong>{formatCurrency(totals.subtotal, form.currency)}</strong></div>
                      <div><span>{tr("Tax")}</span><strong>{formatCurrency(totals.tax, form.currency)}</strong></div>
                      <div><span>{tr("Outstanding")}</span><strong>{formatCurrency(totals.total, form.currency)}</strong></div>
                      <div className='iv-totals-grand'><span>{tr("Total")}</span><strong>{formatCurrency(totals.total, form.currency)}</strong></div>
                    </div>
                  </div>

                  <div className='cp-side-card'>
                    <h3>{tr("Workspace defaults")}</h3>
                    <ul className='cp-side-list'>
                      <li>{tr("Prefix:")} {settings?.invoice_prefix || 'INV'}</li>
                      <li>{tr("Next number:")} {settings?.next_invoice_number || 1}</li>
                      <li>{tr("Terms:")} {settings?.default_payment_terms_days || 14} {tr("days")}</li>
                      <li>{tr("Default tax:")} {settings?.default_tax_rate || '0.00'}%</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
